"use client"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import type React from "react"
import { Box, Typography, IconButton, Button } from "@mui/material"
import { Iconify } from "@/components/common/iconify"
import { UI_ICONS } from "@/lib/ui-icons"
import { AnimatePresence, m as motion } from "framer-motion"

// ─── Types ────────────────────────────────────────────────────────────────────

export type ModalSize = "sm" | "md" | "lg"

const SIZE_WIDTH: Record<ModalSize, number> = { sm: 420, md: 560, lg: 720 }

// Spring in, ease out — brandbook spec
const SPRING_IN  = { duration: 0.22, ease: [0.34, 1.56, 0.64, 1.0] } as const
const EASE_OUT   = { duration: 0.18, ease: [0.32, 0.72, 0.36, 1.0] } as const

// ─── Base Modal component (declarative) ──────────────────────────────────────

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  size?: ModalSize
  footer?: React.ReactNode
  children: React.ReactNode
}

export function Modal({ open, onClose, title, size = "md", footer, children }: ModalProps) {
  // Dismiss on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.18 } }}
            exit={{ opacity: 0, transition: { duration: 0.18 } }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              backgroundColor: "rgba(10, 20, 24, 0.56)",
              backdropFilter: "blur(2px)",
            }}
          />

          {/* Panel */}
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1, transition: SPRING_IN }}
            exit={{ opacity: 0, scale: 0.95, transition: EASE_OUT }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              onClick={e => e.stopPropagation()}
              sx={{
                width: "100%",
                maxWidth: SIZE_WIDTH[size],
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "var(--bg-canvas)",
                border: "1px solid var(--line)",
                borderRadius: "var(--r-lg)",
                boxShadow: "var(--shadow-3)",
                overflow: "hidden",
                pointerEvents: "auto",
                mx: 2,
              }}
            >
              {/* Header */}
              {title && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: "var(--space-6)",
                    py: "var(--space-4)",
                    borderBottom: "1px solid var(--line)",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--font-body)",
                      fontSize: "1.0625rem",
                      fontWeight: 600,
                      color: "var(--text-primary)",
                      lineHeight: 1.3,
                    }}
                  >
                    {title}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Close"
                    onClick={onClose}
                    sx={{
                      color: "var(--text-primary)",
                      "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--bg-hover)" },
                      transition: "color var(--t-fast) var(--ease)",
                    }}
                  >
                    <Iconify icon={UI_ICONS.close} width={20} />
                  </IconButton>
                </Box>
              )}

              {/* Body */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: "var(--space-6)",
                  py: "var(--space-5)",
                }}
              >
                {children}
              </Box>

              {/* Footer */}
              {footer && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "var(--space-2)",
                    px: "var(--space-6)",
                    py: "var(--space-4)",
                    borderTop: "1px solid var(--line)",
                    flexShrink: 0,
                  }}
                >
                  {footer}
                </Box>
              )}
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ─── Imperative API via context ───────────────────────────────────────────────

interface ShowModalConfig {
  title?: string
  body: React.ReactNode
  footer?: React.ReactNode
  size?: ModalSize
}

interface ConfirmConfig {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: "default" | "danger"
}

interface FormModalConfig {
  title: string
  body: React.ReactNode
  submitLabel?: string
  onSubmit: () => Promise<void> | void
  size?: ModalSize
}

export interface ModalContextValue {
  showModal: (config: ShowModalConfig) => () => void
  confirmDialog: (config: ConfirmConfig) => Promise<boolean>
  formModal: (config: FormModalConfig) => () => void
}

const ModalContext = createContext<ModalContextValue | null>(null)

export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error("useModal must be used within ModalProvider")
  return ctx
}

// Internal modal entry shape
type ActiveModal =
  | { id: string; type: "generic"; config: ShowModalConfig }
  | { id: string; type: "confirm"; config: ConfirmConfig; resolve: (v: boolean) => void }
  | { id: string; type: "form"; config: FormModalConfig; submitting: boolean; error?: string }

function GenericModalContent({ m, onClose }: { m: Extract<ActiveModal, { type: "generic" }>; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={m.config.title} size={m.config.size} footer={m.config.footer}>
      {m.config.body}
    </Modal>
  )
}

function ConfirmModalContent({
  m,
  onClose,
}: {
  m: Extract<ActiveModal, { type: "confirm" }>
  onClose: (value: boolean) => void
}) {
  const isDanger = m.config.variant === "danger"
  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={m.config.title}
      size="sm"
      footer={
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onClose(false)}
            sx={{ color: "var(--text-primary)", borderColor: "var(--line-strong)" }}
          >
            {m.config.cancelLabel ?? "Cancel"}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => onClose(true)}
            sx={isDanger ? {
              backgroundColor: "var(--danger-text)",
              "&:hover": { backgroundColor: "var(--danger-text)", filter: "brightness(1.1)" },
            } : undefined}
          >
            {m.config.confirmLabel ?? "Confirm"}
          </Button>
        </>
      }
    >
      <Typography sx={{ fontFamily: "var(--font-body)", fontSize: "0.9375rem", color: "var(--text-primary)", lineHeight: 1.5 }}>
        {m.config.message}
      </Typography>
    </Modal>
  )
}

function FormModalContent({
  m,
  onClose,
  onSubmitDone,
}: {
  m: Extract<ActiveModal, { type: "form" }>
  onClose: () => void
  onSubmitDone: (id: string, error?: string) => void
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(undefined)
    try {
      await m.config.onSubmit()
      onSubmitDone(m.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.")
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={submitting ? () => {} : onClose}
      title={m.config.title}
      size={m.config.size}
      footer={
        <>
          {error && (
            <Typography sx={{ flex: 1, fontSize: "0.8125rem", color: "var(--danger-text)" }}>
              {error}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={submitting}
            sx={{ color: "var(--text-primary)", borderColor: "var(--line-strong)" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving…" : (m.config.submitLabel ?? "Save")}
          </Button>
        </>
      }
    >
      {m.config.body}
    </Modal>
  )
}

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modals, setModals] = useState<ActiveModal[]>([])

  const closeById = useCallback((id: string) => {
    setModals(ms => ms.filter(m => m.id !== id))
  }, [])

  const api = useMemo<ModalContextValue>(() => ({
    showModal(config) {
      const id = Math.random().toString(36).slice(2)
      setModals(ms => [...ms, { id, type: "generic", config }])
      return () => closeById(id)
    },

    confirmDialog(config) {
      return new Promise<boolean>(resolve => {
        const id = Math.random().toString(36).slice(2)
        setModals(ms => [...ms, { id, type: "confirm", config, resolve }])
      })
    },

    formModal(config) {
      const id = Math.random().toString(36).slice(2)
      setModals(ms => [...ms, { id, type: "form", config, submitting: false }])
      return () => closeById(id)
    },
  }), [closeById])

  return (
    <ModalContext.Provider value={api}>
      {children}
      {modals.map(m => {
        if (m.type === "generic") {
          return (
            <GenericModalContent key={m.id} m={m} onClose={() => closeById(m.id)} />
          )
        }
        if (m.type === "confirm") {
          return (
            <ConfirmModalContent
              key={m.id}
              m={m}
              onClose={value => {
                m.resolve(value)
                closeById(m.id)
              }}
            />
          )
        }
        if (m.type === "form") {
          return (
            <FormModalContent
              key={m.id}
              m={m}
              onClose={() => closeById(m.id)}
              onSubmitDone={id => closeById(id)}
            />
          )
        }
        return null
      })}
    </ModalContext.Provider>
  )
}
